import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="search-filter"
export default class extends Controller {
  static targets = [
    "searchInput", 
    "filterDropdown", 
    "filterToggle",
    "results", 
    "loadingIndicator",
    "clearButton",
    "statusMessage"
  ]
  
  static values = {
    searchUrl: String,
    debounceDelay: { type: Number, default: 300 },
    minSearchLength: { type: Number, default: 2 },
    filterParams: Object
  }

  connect() {
    // Bind methods to maintain context
    this.boundHandleInput = this.handleInput.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleFocus = this.handleFocus.bind(this)
    this.boundHandleBlur = this.handleBlur.bind(this)
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    
    // Add event listeners
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.addEventListener("input", this.boundHandleInput)
      this.searchInputTarget.addEventListener("keydown", this.boundHandleKeydown)
      this.searchInputTarget.addEventListener("focus", this.boundHandleFocus)
      this.searchInputTarget.addEventListener("blur", this.boundHandleBlur)
    }
    
    // Listen for clicks outside to close dropdown
    document.addEventListener("click", this.boundHandleClickOutside)
    
    // Initialize state
    this.isSearching = false
    this.searchCache = new Map()
    this.currentQuery = ""
    this.currentFilters = this.filterParamsValue || {}
    
    // Initialize UI state
    this.updateClearButtonVisibility()
    this.hideFilterDropdown()
    
  }

  disconnect() {
    // Remove event listeners
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.removeEventListener("input", this.boundHandleInput)
      this.searchInputTarget.removeEventListener("keydown", this.boundHandleKeydown)
      this.searchInputTarget.removeEventListener("focus", this.boundHandleFocus)
      this.searchInputTarget.removeEventListener("blur", this.boundHandleBlur)
    }
    
    document.removeEventListener("click", this.boundHandleClickOutside)
    
    // Clear any pending timeouts
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
  }

  // Input handling with debouncing
  handleInput(event) {
    const query = event.target.value.trim()
    this.currentQuery = query
    
    // Update UI state
    this.updateClearButtonVisibility()
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    // Set new timeout for debounced search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, this.debounceDelayValue)
    
    // Show immediate feedback
    if (query.length >= this.minSearchLengthValue) {
      this.showSearchingState()
    } else if (query.length === 0) {
      this.clearResults()
    }
  }

  // Keyboard navigation
  handleKeydown(event) {
    switch (event.key) {
      case "Escape":
        this.clearSearch()
        this.hideFilterDropdown()
        break
      case "Enter":
        event.preventDefault()
        this.performSearch(this.currentQuery, true) // Force search even if cached
        break
      case "ArrowDown":
        event.preventDefault()
        this.navigateResults("down")
        break
      case "ArrowUp":
        event.preventDefault()
        this.navigateResults("up")
        break
    }
  }

  handleFocus(event) {
    // Show results if we have any
    if (this.hasResultsTarget && this.resultsTarget.children.length > 0) {
      this.showResults()
    }
  }

  handleBlur(event) {
    // Delay hiding results to allow for clicks
    setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        this.hideResults()
      }
    }, 150)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideFilterDropdown()
      this.hideResults()
    }
  }

  // Search functionality
  async performSearch(query, forceSearch = false) {
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    // Validate query
    if (!query || query.length < this.minSearchLengthValue) {
      this.clearResults()
      return
    }
    
    // Check cache first (unless forced)
    const cacheKey = this.getCacheKey(query, this.currentFilters)
    if (!forceSearch && this.searchCache.has(cacheKey)) {
      const cachedResults = this.searchCache.get(cacheKey)
      this.displayResults(cachedResults, query)
      return
    }
    
    try {
      this.isSearching = true
      this.showLoadingState()
      
      // Build search URL with parameters
      const searchUrl = new URL(this.searchUrlValue, window.location.origin)
      searchUrl.searchParams.set("q", query)
      
      // Add filter parameters
      Object.entries(this.currentFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          searchUrl.searchParams.set(key, value)
        }
      })
      
      // Add request headers
      const headers = {
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
      
      // Add CSRF token if available
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
      if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken
      }
      
      const response = await fetch(searchUrl, {
        method: "GET",
        headers: headers
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const data = await response.json()
      
      // Cache the results
      this.searchCache.set(cacheKey, data)
      
      // Display results
      this.displayResults(data, query)
      
      // Dispatch custom event for other controllers
      this.dispatchSearchCompleteEvent(data, query)
      
    } catch (error) {
      console.error("Search error:", error)
      this.showErrorState(error.message)
    } finally {
      this.isSearching = false
      this.hideLoadingState()
    }
  }

  // Results display
  displayResults(data, query) {
    if (!this.hasResultsTarget) return
    
    const { results, total_count, filters } = data
    
    if (!results || results.length === 0) {
      this.showNoResultsState(query)
      return
    }
    
    // Generate results HTML
    const resultsHTML = this.renderResults(results, query)
    this.resultsTarget.innerHTML = resultsHTML
    
    // Update status message
    this.updateStatusMessage(total_count, query)
    
    // Show results
    this.showResults()
    
    // Update available filters if provided
    if (filters) {
      this.updateAvailableFilters(filters)
    }
  }

  renderResults(results, query) {
    const highlightedQuery = this.escapeHtml(query)
    
    return results.map((item, index) => {
      const itemType = item.type || "track"
      const icon = this.getItemIcon(itemType)
      const title = this.highlightText(item.title || item.name, query)
      const subtitle = this.getItemSubtitle(item, query)
      
      return `
        <div class="search-result-item p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0" 
             data-result-index="${index}"
             data-${itemType}-id="${item.id}"
             data-action="click->search-filter#selectResult">
          <div class="flex items-center space-x-3">
            <div class="flex-shrink-0 text-xl">${icon}</div>
            <div class="flex-grow min-w-0">
              <div class="font-medium text-gray-900 truncate">${title}</div>
              ${subtitle ? `<div class="text-sm text-gray-500 truncate">${subtitle}</div>` : ""}
            </div>
            <div class="flex-shrink-0">
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                ${itemType}
              </span>
            </div>
          </div>
        </div>
      `
    }).join("")
  }

  getItemIcon(itemType) {
    const icons = {
      "album": "🎵",
      "track": "🎶",
      "artist": "👤",
      "playlist": "📝"
    }
    return icons[itemType] || "📄"
  }

  getItemSubtitle(item, query) {
    if (item.artist) {
      return this.highlightText(item.artist, query)
    }
    if (item.album) {
      return this.highlightText(item.album, query)
    }
    if (item.duration) {
      return this.formatDuration(item.duration)
    }
    return ""
  }

  highlightText(text, query) {
    if (!text || !query) return this.escapeHtml(text || "")
    
    const escapedText = this.escapeHtml(text)
    const escapedQuery = this.escapeHtml(query)
    const regex = new RegExp(`(${escapedQuery})`, "gi")
    
    return escapedText.replace(regex, '<mark class="bg-yellow-200">$1</mark>')
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  formatDuration(seconds) {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
  }

  // Result selection and navigation
  selectResult(event) {
    const resultItem = event.currentTarget
    const itemType = this.getItemTypeFromElement(resultItem)
    const itemId = this.getItemIdFromElement(resultItem, itemType)
    
    if (!itemType || !itemId) {
      console.error("Could not determine item type or ID from result")
      return
    }
    
    // Dispatch selection event
    this.dispatchResultSelectedEvent(itemType, itemId, resultItem)
    
    // Hide results
    this.hideResults()
    
    // Clear search (optional - can be configured)
    // this.clearSearch()
  }

  getItemTypeFromElement(element) {
    if (element.dataset.albumId) return "album"
    if (element.dataset.trackId) return "track"
    if (element.dataset.artistId) return "artist"
    if (element.dataset.playlistId) return "playlist"
    return null
  }

  getItemIdFromElement(element, itemType) {
    switch (itemType) {
      case "album": return element.dataset.albumId
      case "track": return element.dataset.trackId
      case "artist": return element.dataset.artistId
      case "playlist": return element.dataset.playlistId
      default: return null
    }
  }

  navigateResults(direction) {
    if (!this.hasResultsTarget) return
    
    const results = this.resultsTarget.querySelectorAll(".search-result-item")
    if (results.length === 0) return
    
    const currentActive = this.resultsTarget.querySelector(".search-result-item.active")
    let newIndex = 0
    
    if (currentActive) {
      const currentIndex = parseInt(currentActive.dataset.resultIndex)
      if (direction === "down") {
        newIndex = (currentIndex + 1) % results.length
      } else {
        newIndex = currentIndex === 0 ? results.length - 1 : currentIndex - 1
      }
    }
    
    // Remove previous active state
    results.forEach(result => result.classList.remove("active", "bg-blue-50"))
    
    // Add active state to new result
    const newActive = results[newIndex]
    newActive.classList.add("active", "bg-blue-50")
    
    // Scroll into view if needed
    newActive.scrollIntoView({ block: "nearest" })
  }

  // Filter management
  toggleFilterDropdown() {
    if (this.hasFilterDropdownTarget) {
      const isVisible = !this.filterDropdownTarget.classList.contains("hidden")
      
      if (isVisible) {
        this.hideFilterDropdown()
      } else {
        this.showFilterDropdown()
      }
    }
  }

  showFilterDropdown() {
    if (this.hasFilterDropdownTarget) {
      this.filterDropdownTarget.classList.remove("hidden")
      this.filterDropdownTarget.classList.add("block")
      
      // Update toggle button state
      if (this.hasFilterToggleTarget) {
        this.filterToggleTarget.classList.add("active")
      }
    }
  }

  hideFilterDropdown() {
    if (this.hasFilterDropdownTarget) {
      this.filterDropdownTarget.classList.add("hidden")
      this.filterDropdownTarget.classList.remove("block")
      
      // Update toggle button state
      if (this.hasFilterToggleTarget) {
        this.filterToggleTarget.classList.remove("active")
      }
    }
  }

  updateFilter(event) {
    const filterKey = event.target.dataset.filterKey
    const filterValue = event.target.value
    
    if (filterKey) {
      // Update filter state
      if (filterValue === "" || filterValue === null) {
        delete this.currentFilters[filterKey]
      } else {
        this.currentFilters[filterKey] = filterValue
      }
      
      // Clear cache since filters changed
      this.searchCache.clear()
      
      // Re-run search with new filters
      if (this.currentQuery && this.currentQuery.length >= this.minSearchLengthValue) {
        this.performSearch(this.currentQuery, true)
      }
      
      // Dispatch filter change event
      this.dispatchFilterChangeEvent(filterKey, filterValue)
    }
  }

  clearFilters() {
    // Reset all filter inputs
    const filterInputs = this.element.querySelectorAll('[data-filter-key]')
    filterInputs.forEach(input => {
      if (input.type === "checkbox") {
        input.checked = false
      } else {
        input.value = ""
      }
    })
    
    // Clear filter state
    this.currentFilters = {}
    
    // Clear cache
    this.searchCache.clear()
    
    // Re-run search if needed
    if (this.currentQuery && this.currentQuery.length >= this.minSearchLengthValue) {
      this.performSearch(this.currentQuery, true)
    }
    
    // Dispatch filter clear event
    this.dispatchFilterClearEvent()
  }

  updateAvailableFilters(filters) {
    // Update filter options based on current search results
    // This would be implemented based on specific filter UI structure
  }

  // UI state management
  showSearchingState() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove("hidden")
    }
    
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = "Searching..."
      this.statusMessageTarget.classList.remove("hidden")
    }
  }

  showLoadingState() {
    this.showSearchingState()
  }

  hideLoadingState() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add("hidden")
    }
  }

  showErrorState(message) {
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = `Error: ${message}`
      this.statusMessageTarget.className = "text-red-600 text-sm mt-2"
      this.statusMessageTarget.classList.remove("hidden")
    }
    
    this.clearResults()
  }

  showNoResultsState(query) {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = `
        <div class="p-4 text-center text-gray-500">
          <div class="text-lg mb-2">📭</div>
          <div>No results found for "${this.escapeHtml(query)}"</div>
          <div class="text-sm mt-1">Try adjusting your search terms or filters</div>
        </div>
      `
      this.showResults()
    }
  }

  updateStatusMessage(count, query) {
    if (this.hasStatusMessageTarget) {
      const message = count === 1 
        ? `1 result for "${this.escapeHtml(query)}"` 
        : `${count} results for "${this.escapeHtml(query)}"`
      
      this.statusMessageTarget.textContent = message
      this.statusMessageTarget.className = "text-gray-600 text-sm mt-2"
      this.statusMessageTarget.classList.remove("hidden")
    }
  }

  showResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.remove("hidden")
      this.resultsTarget.classList.add("block")
    }
  }

  hideResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
      this.resultsTarget.classList.remove("block")
    }
  }

  clearResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ""
    }
    this.hideResults()
    this.hideLoadingState()
    
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.classList.add("hidden")
    }
  }

  clearSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
    }
    
    this.currentQuery = ""
    this.clearResults()
    this.updateClearButtonVisibility()
    
    // Dispatch clear event
    this.dispatchSearchClearEvent()
  }

  updateClearButtonVisibility() {
    if (this.hasClearButtonTarget) {
      if (this.currentQuery && this.currentQuery.length > 0) {
        this.clearButtonTarget.classList.remove("hidden")
      } else {
        this.clearButtonTarget.classList.add("hidden")
      }
    }
  }

  // Utility methods
  getCacheKey(query, filters) {
    return `${query}|${JSON.stringify(filters)}`
  }

  // Event dispatching
  dispatchSearchCompleteEvent(results, query) {
    const event = new CustomEvent("search:complete", {
      bubbles: true,
      detail: { results, query, filters: this.currentFilters }
    })
    this.element.dispatchEvent(event)
  }

  dispatchResultSelectedEvent(itemType, itemId, element) {
    const event = new CustomEvent("search:result:selected", {
      bubbles: true,
      detail: { itemType, itemId, element }
    })
    this.element.dispatchEvent(event)
  }

  dispatchFilterChangeEvent(filterKey, filterValue) {
    const event = new CustomEvent("search:filter:change", {
      bubbles: true,
      detail: { filterKey, filterValue, allFilters: this.currentFilters }
    })
    this.element.dispatchEvent(event)
  }

  dispatchFilterClearEvent() {
    const event = new CustomEvent("search:filter:clear", {
      bubbles: true,
      detail: { filters: this.currentFilters }
    })
    this.element.dispatchEvent(event)
  }

  dispatchSearchClearEvent() {
    const event = new CustomEvent("search:clear", {
      bubbles: true,
      detail: {}
    })
    this.element.dispatchEvent(event)
  }

  // Public API methods for external control
  search(query) {
    this.currentQuery = query
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = query
    }
    this.performSearch(query, true)
  }

  setFilter(key, value) {
    this.currentFilters[key] = value
    this.searchCache.clear()
    
    // Update UI if filter input exists
    const filterInput = this.element.querySelector(`[data-filter-key="${key}"]`)
    if (filterInput) {
      if (filterInput.type === "checkbox") {
        filterInput.checked = !!value
      } else {
        filterInput.value = value
      }
    }
    
    // Re-run search if needed
    if (this.currentQuery && this.currentQuery.length >= this.minSearchLengthValue) {
      this.performSearch(this.currentQuery, true)
    }
  }

  getResults() {
    const cacheKey = this.getCacheKey(this.currentQuery, this.currentFilters)
    return this.searchCache.get(cacheKey) || null
  }
}